import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { PAGE_SIZE_OPTIONS } from '@/types/pagination';
import { cn } from '@/lib/utils';

type PageSizeSelectorProps =
  | {
      pageSize: number;
      onPageSizeChange?: (pageSize: number) => void;
      showPageSizeSelector?: boolean;
    }
  | {
      pageSize?: never;
      onPageSizeChange?: never;
      showPageSizeSelector?: never;
    };

type ReportPaginationProps = {
  page: number;
  totalPages: number;
  onPageChange: (p: number) => void;
} & PageSizeSelectorProps;

interface PageLinksProps {
  page: number;
  totalPages: number;
  onPageChange: (p: number) => void;
  className?: string;
}

function PageLinks({ page, totalPages, onPageChange, className }: PageLinksProps) {
  return (
    <Pagination className={className}>
      <PaginationContent>
        <PaginationItem>
          <PaginationPrevious
            onClick={() => onPageChange(Math.max(1, page - 1))}
            className={cn(page === 1 && 'pointer-events-none opacity-50')}
          />
        </PaginationItem>

        {Array.from({ length: totalPages }, (_, i) => i + 1)
          .filter((p) => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
          .reduce<(number | 'ellipsis')[]>((acc, p, idx, arr) => {
            if (idx > 0 && p - (arr[idx - 1] as number) > 1) acc.push('ellipsis');
            acc.push(p);
            return acc;
          }, [])
          .map((item, i) =>
            item === 'ellipsis' ? (
              <PaginationItem key={`e-${i}`}>
                <PaginationEllipsis />
              </PaginationItem>
            ) : (
              <PaginationItem key={item}>
                <PaginationLink
                  isActive={item === page}
                  onClick={() => onPageChange(item as number)}
                >
                  {item}
                </PaginationLink>
              </PaginationItem>
            ),
          )}

        <PaginationItem>
          <PaginationNext
            onClick={() => onPageChange(Math.min(totalPages, page + 1))}
            className={cn(page === totalPages && 'pointer-events-none opacity-50')}
          />
        </PaginationItem>
      </PaginationContent>
    </Pagination>
  );
}

export function ReportPagination({
  page,
  totalPages,
  onPageChange,
  pageSize,
  onPageSizeChange,
  showPageSizeSelector = onPageSizeChange !== undefined,
}: ReportPaginationProps) {
  if (!showPageSizeSelector || !onPageSizeChange || pageSize === undefined) {
    return <PageLinks page={page} totalPages={totalPages} onPageChange={onPageChange} />;
  }

  return (
    <div className="flex items-center justify-between gap-2">
      <div className="flex items-center gap-1.5">
        <span className="text-xs text-muted-foreground whitespace-nowrap">Rows per page</span>
        <Select
          value={String(pageSize)}
          onValueChange={(value) => onPageSizeChange(Number(value))}
        >
          <SelectTrigger className="h-7 w-[72px] text-xs px-2" aria-label="Rows per page">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PAGE_SIZE_OPTIONS.map((size) => (
              <SelectItem key={size} value={String(size)} className="text-xs">
                {size}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {totalPages > 1 && (
        <PageLinks page={page} totalPages={totalPages} onPageChange={onPageChange} className="mx-0 w-auto" />
      )}
    </div>
  );
}
